import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { tcpaConsent } from 'domain/consent';
import { SiteLanguage } from 'types/locales';
import ExprealtyCaTheme from './theme-config';
import { createHomeConfig } from './home-config';
import { createJoinExpConfig } from './join-exp-config';
import {
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID_TYPE,
  ContentHosts,
  DEFAULT_GTM_CONTAINER_ID_KEY,
  AllowedExperimentKey,
} from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { createHeaderConfig } from './header-config';
import { createOrganizationConfig } from './organization-config';
import { createHeadTagsConfig } from './head-tags-config';
import { createAddressPageConfig } from './address-page-config';
import { Namespaces } from 'types/locales';
import { createLanguageConfig } from '@tenants/utils/configs';
import { createCurrencyMap } from '@tenants/utils/currency-map';

import type {
  HeadTagsConfig,
  HomeConfig,
  JoinExpConfig,
  Tenant,
  TenantConfigHeadTags,
  TenantConfigHome,
  TenantConfigJoinExp,
  FooterConfig,
  TenantConfigFooter,
  TenantConfigFilters,
  HeaderConfig,
  TenantConfigHeader,
  OrganizationConfig,
  TenantConfigOrganization,
  TenantConfigSEO,
  AddressPageConfig,
  TenantConfigAddressPage,
  TenantConfigObservability,
} from '@tenants/types';
import { canShowSchoolsCanadian } from '@tenants/definitions/shared/features';
import { CRITICAL_CITIES_CA } from '@tenants/constants/critical-cities';

//#region Public Constants
export const EXP_REALTY_CA_L18N_NAMESPACES = [
  Namespaces.AGENT_SEARCH,
  Namespaces.EXP_REALTY_CA,
  Namespaces.JOIN_EXP_CA,
  Namespaces.HOME_APPRAISAL,
  Namespaces.COMMON,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.LISTING,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
  Namespaces.INTERNAL_LINKS,
  Namespaces.HOME,
] as const;

// TODO: Improve the approach once we roll this out to production
// eslint-disable-next-line enforce-localization-testing/enforce-localization-testing
const allowGlobalLocaleSwitching = true;

export const JOIN_EXP_CA_GTM_CONTAINER_ID_KEY = 'JoinExpCa' as const;
//#endregion

//#region Public Types
export type ExpRealtyCaTenantConfig = TenantConfigHome & TenantConfigJoinExp & TenantConfigFooter & TenantConfigFilters & TenantConfigOrganization & TenantConfigHeader & TenantConfigSEO & TenantConfigHeadTags & TenantConfigObservability & TenantConfigAddressPage;
export type ExpRealtyCaTenant = Tenant<EXP_REALTY_CA_TENANT_ID_TYPE, typeof ExprealtyCaTheme, ExpRealtyCaTenantConfig, typeof JOIN_EXP_CA_GTM_CONTAINER_ID_KEY>;
//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig();
const home: HomeConfig = createHomeConfig();
const joinExp: JoinExpConfig = createJoinExpConfig();
const footer: FooterConfig = createFooterConfig();
const organization: OrganizationConfig = createOrganizationConfig();
const headTags: HeadTagsConfig = createHeadTagsConfig();
const addressPage: AddressPageConfig = createAddressPageConfig();
//#endregion

//#region Tenant Definition
export const expRealtyCATenant: ExpRealtyCaTenant = {
  id: EXP_REALTY_CA_TENANT_ID,
  hosts: configJSON.hosts[EXP_REALTY_CA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_REALTY_CA_TENANT_ID] as ContentHosts,
  theme: ExprealtyCaTheme,
  get localization() {
    return {
      ...createLanguageConfig({
        globalLanguages: [SiteLanguage.EnglishCA, SiteLanguage.FrenchCA],
        standardLanguages: [SiteLanguage.English, SiteLanguage.French],
        globalDefaultLanguage: SiteLanguage.EnglishCA,
        standardDefaultLanguage: SiteLanguage.English,
        namespaces: EXP_REALTY_CA_L18N_NAMESPACES,
        allowGlobalLocaleSwitching,
      }),
      countries: [CountryCodeList.CANADA],
      defaultCountry: CountryCodeList.CANADA,
      switchingEnabled: true,
      currencyMap: createCurrencyMap([CountryCodeList.CANADA]),
    };
  },
  config: {
    header,
    home,
    joinExp,
    footer,
    organization,
    headTags,
    addressPage,
    filters: {
      countryCode: CountryCodeList.CANADA,
    },
    seo: {
      useLocaleAwareCanonicals: allowGlobalLocaleSwitching,
      useLocaleAwareHrefLang: allowGlobalLocaleSwitching,
      includeInLanguageInSchemas: true,
    },
    observability: {
      enabled: true,
      areaPages: {
        enabled: true,
        criticalCities: CRITICAL_CITIES_CA,
      },
    },
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_REALTY_CA_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_REALTY_CA_TENANT_ID,
  },
  get features() {
    return {
      MyLinkMyLead: true,
      AgentSearch: true,
      AccessibilityWidget: true,
      TrustArc: {
        enabled: true,
        domain: 'exprealty.com',
      },
      ShowHybridAreaMapPage: true,
      UseAreaPageV2: true,
      UseNewHybridPageDesign: true,
      UseSmartSearch: false,
      UseNewAreaSearchDesign: true,
      /** Building-grouped pins: off until Zoocasa prod QA; keep clusters + boundary like production. */
      UseBuildingGroupedPins: false,
      AddressPage: {
        showSchools: canShowSchoolsCanadian,
      },
      ShowProfileLocaleSwitcher: allowGlobalLocaleSwitching,
      HasMultilingualContentPages: true,
      UseListingCardV2: allowGlobalLocaleSwitching,
      ShowQuebecPopup: !allowGlobalLocaleSwitching,
      ShowAgentCardsOnAddressPage: true,
      AiSearch: false,
      LoginRoleSelection: true,
      PropertyPreviewDrawer: true,
      PoiAmenitiesFilter: true,
      ListingPropertyDetailsRedesign: true,
      UseDetailedRecentSearches: true,
      Experiments: {
        [AllowedExperimentKey.CONNECTION_REQUEST_FORM]: {
          enabled: true,
          observability: true,
        },
        [AllowedExperimentKey.PROPERTY_DRAWER_AUTO_COLLAPSE]: {
          enabled: true,
          observability: true,
        },
        [AllowedExperimentKey.LOCAL_FOCUS_HOMEPAGE]: {
          enabled: true,
          observability: true,
        },
      },
    };
  },
  googleTagManager: {
    containerIds: {
      [DEFAULT_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdExpCa,
      [JOIN_EXP_CA_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdJoinExp,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID_EXP || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID_EXP || '',
  },
  sitemap: {
    urls: [
      'https://www.exprealty.ca/sitemaps/area-pages/sitemap-index.xml',
      'https://www.exprealty.ca/sitemaps/address-pages/sitemap-index.xml',
      'https://www.exprealty.ca/sitemaps/address-pages/sitemap-fr-index.xml',
      'https://www.exprealty.ca/sitemaps/area-pages/sitemap-fr-index.xml',
      'https://www.exprealty.ca/sitemaps/root-pages/sitemap-index.xml',
    ],
  },
  robots: {
    disallowDomains: [],
    disallowPaths: ['/fr-CA/*'],
  },
  pvr: {
    phoneNumber: '1-844-248-2174',
  },
  websiteUUID: configJSON.websiteUUID?.[EXP_REALTY_CA_TENANT_ID], // Update repo variable to change website UUID
  mobileAppBanner: null,
  consent: tcpaConsent({ exempt: [CountryCodeList.CANADA]}),
};
//#endregion
